import { createContext, useContext, useReducer, useCallback } from "react";

interface User {
  id: string;
  full_name: string;
  phone: string;
  country_code: string;
  avatar_url: string;
}

interface PendingAuth {
  phone: string;
  country_code: string;
  full_name: string;
  mode: "login" | "register";
}

interface AuthState {
  user: User | null;
  pendingAuth: PendingAuth | null;
}

type AuthAction =
  | { type: "SET_USER"; user: User }
  | { type: "CLEAR_USER" }
  | { type: "SET_PENDING_AUTH"; pendingAuth: PendingAuth }
  | { type: "CLEAR_PENDING_AUTH" };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "SET_USER":
      return { ...state, user: action.user, pendingAuth: null };
    case "CLEAR_USER":
      return { ...state, user: null };
    case "SET_PENDING_AUTH":
      return { ...state, pendingAuth: action.pendingAuth };
    case "CLEAR_PENDING_AUTH":
      return { ...state, pendingAuth: null };
    default:
      return state;
  }
}

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  pendingAuth: PendingAuth | null;
  initiateLogin: (phone: string, countryCode: string) => void;
  initiateRegister: (fullName: string, phone: string, countryCode: string) => void;
  verifyOtp: (code: string) => Promise<boolean>;
  logout: () => void;
  clearPendingAuth: () => void;
  resendOtp: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    pendingAuth: null,
  });

  const initiateLogin = useCallback((phone: string, countryCode: string) => {
    dispatch({
      type: "SET_PENDING_AUTH",
      pendingAuth: {
        phone,
        country_code: countryCode,
        full_name: "",
        mode: "login",
      },
    });
  }, []);

  const initiateRegister = useCallback(
    (fullName: string, phone: string, countryCode: string) => {
      dispatch({
        type: "SET_PENDING_AUTH",
        pendingAuth: {
          phone,
          country_code: countryCode,
          full_name: fullName,
          mode: "register",
        },
      });
    },
    []
  );

  const verifyOtp = useCallback(
    async (code: string): Promise<boolean> => {
      if (!state.pendingAuth) return false;

      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 1200));

      // Accept any 6-digit code as valid for demo
      if (code.length === 6) {
        const mockUser: User = {
          id: "user_" + Math.random().toString(36).substring(2, 10),
          full_name:
            state.pendingAuth.full_name ||
            "Customer " + state.pendingAuth.phone.slice(-4),
          phone: state.pendingAuth.phone,
          country_code: state.pendingAuth.country_code,
          avatar_url:
            "https://readdy.ai/api/search-image?query=Friendly%20professional%20portrait%20headshot%20on%20clean%20cream%20background%2C%20soft%20natural%20lighting%2C%20warm%20tones%2C%20approachable%20expression&width=160&height=160&seq=auth-avatar-default&orientation=squarish",
        };
        dispatch({ type: "SET_USER", user: mockUser });
        return true;
      }

      return false;
    },
    [state.pendingAuth]
  );

  const logout = useCallback(() => {
    dispatch({ type: "CLEAR_USER" });
  }, []);

  const clearPendingAuth = useCallback(() => {
    dispatch({ type: "CLEAR_PENDING_AUTH" });
  }, []);

  const resendOtp = useCallback(() => {
    // In mock mode, just keep the pending auth — no real SMS sent
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        isAuthenticated: state.user !== null,
        pendingAuth: state.pendingAuth,
        initiateLogin,
        initiateRegister,
        verifyOtp,
        logout,
        clearPendingAuth,
        resendOtp,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthProvider;